<script>
import sizeMixin from "./sizeMixin";
import DashTip from "./DashTip";
import StatusMark from "./StatusMark";
import DashEmpty from "./DashEmpty";
import { arcPath, polar } from "./scale";
import { fmtNum, fmtPct, THRESHOLDS } from "@/data/dashboardModel";

// Rueda PHVA: anillo segmentado de 4 cuadrantes (Planear, Hacer, Verificar, Actuar)
// ordenados en sentido horario desde las 9 (Planear arriba-izquierda). Cada cuadrante
// se llena con el % ponderado de su fase; marcas exteriores en 60 % y 85 % (Res. 0312).
// La cifra héroe del centro es el % ponderado total, con semáforo (icono + etiqueta).
const VB = 400;
const C = 200;
const R_OUT = 150;
const THICK = 26;
const R_MID = R_OUT - THICK / 2;
const GAP = 2.6; // grados de aire a cada lado de un cuadrante
const R_LABEL = 186;

export default {
  name: "PhvaWheel",
  components: { DashTip, StatusMark, DashEmpty },
  mixins: [sizeMixin],
  props: {
    phva: { type: Array, default: () => [] },
    total: { type: Object, required: true },
    ready: { type: Boolean, default: false },
    scopeLabel: { type: String, default: "" },
    to: { type: [String, Object], default: "/forms/estandares" },
  },
  data() {
    return { active: -1, tip: { x: 0, y: 0 } };
  },
  computed: {
    hasPhva() {
      return this.phva.length > 0;
    },
    stageWidth() {
      return this.width || 340;
    },
    heroSize() {
      return Math.round(Math.max(40, Math.min(56, this.stageWidth * 0.165)));
    },
    quads() {
      const keys = ["planear", "hacer", "verificar", "actuar"];
      const names = { planear: "Planear", hacer: "Hacer", verificar: "Verificar", actuar: "Actuar" };
      return keys.map((key, i) => {
        const p = this.phva.find((x) => x.key === key) || null;
        const a0 = 270 + 90 * i + GAP;
        const a1 = a0 + 90 - 2 * GAP;
        const mid = polar(C, C, R_LABEL, (a0 + a1) / 2);
        const ringMid = polar(C, C, R_MID, (a0 + a1) / 2);
        const pct = p && p.pct !== null ? Math.max(0, Math.min(100, p.pct)) : null;
        return {
          key,
          i,
          name: p ? p.label : names[key],
          data: p,
          pct,
          d: arcPath(C, C, R_MID, a0, a1),
          label: mid,
          anchor: ringMid,
          ticks: [THRESHOLDS.critical, THRESHOLDS.acceptable].map((t) => {
            const ang = a0 + ((a1 - a0) * t) / 100;
            const p1 = polar(C, C, R_OUT + 3, ang);
            const p2 = polar(C, C, R_OUT + 8, ang);
            return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, t };
          }),
        };
      });
    },
    ariaLabel() {
      const parts = this.quads.map((q) => (q.pct === null ? `${q.name} no aplica` : `${q.name} ${fmtPct(q.pct, 1)}`));
      const t = this.total;
      return (
        `Rueda PHVA. ${this.hasPhva ? parts.join(", ") + ". " : "Desglose por fase no disponible. "}` +
        `Cumplimiento ponderado total ${fmtPct(t.pct)}${t.status ? ", " + t.status.label.toLowerCase() : ""}.`
      );
    },
  },
  methods: {
    fmtNum,
    fmtPct,
    dash(q) {
      const v = this.ready && q.pct !== null ? q.pct : 0;
      return `${v} 100`;
    },
    focus(i) {
      if (!this.hasPhva) return;
      this.active = i;
      const q = this.quads[i];
      const k = this.stageWidth / VB;
      this.tip = { x: q.anchor.x * k, y: q.anchor.y * k };
    },
    blur(i) {
      if (this.active === i) this.active = -1;
    },
    subtitle(q) {
      const std = { planear: "Estándares 1 y 2", hacer: "Estándares 3, 4 y 5", verificar: "Estándar 6", actuar: "Estándar 7" };
      return std[q.key];
    },
  },
  VB,
  C,
  THICK,
};
</script>

<template>
  <div class="dsh-wheel">
    <div ref="measure" class="dsh-wheel__stage">
      <svg
        class="dsh-wheel__svg"
        :viewBox="`0 0 ${$options.VB} ${$options.VB}`"
        role="img"
        :aria-label="ariaLabel"
      >
        <g v-for="q in quads" :key="q.key" :class="{ 'is-dim': active > -1 && active !== q.i, 'is-active': active === q.i }">
          <path
            class="dsh-wheel__track"
            :class="{ 'is-na': hasPhva && q.pct === null, 'is-frame': !hasPhva }"
            :d="q.d"
            :stroke-width="$options.THICK"
            fill="none"
          />
          <path
            v-if="q.pct !== null"
            class="dsh-wheel__fill"
            :d="q.d"
            :stroke-width="$options.THICK"
            fill="none"
            pathLength="100"
            :stroke-dasharray="dash(q)"
            :style="{ transitionDelay: q.i * 40 + 'ms' }"
          />
          <line
            v-for="t in q.ticks"
            :key="q.key + t.t"
            class="dsh-wheel__tick"
            :x1="t.x1"
            :y1="t.y1"
            :x2="t.x2"
            :y2="t.y2"
          />
          <text class="dsh-wheel__name" :x="q.label.x" :y="q.label.y" text-anchor="middle" dominant-baseline="middle">{{ q.name }}</text>
          <path
            v-if="hasPhva"
            class="dsh-wheel__hit"
            :d="q.d"
            stroke-width="44"
            fill="none"
            @pointerenter="focus(q.i)"
            @pointerleave="blur(q.i)"
          />
        </g>
      </svg>

      <div class="dsh-wheel__core" aria-hidden="true">
        <span class="dsh-wheel__kicker">Cumplimiento ponderado</span>
        <span class="dsh-wheel__hero" :style="{ fontSize: heroSize + 'px' }">{{ fmtNum(total.pct) }}<span class="dsh-wheel__unit">%</span></span>
        <StatusMark :status="total.status" />
        <span class="dsh-wheel__pts">{{ fmtNum(total.approvedScore) }} de {{ fmtNum(total.requiredScore) }} puntos</span>
      </div>

      <DashTip :show="active > -1" :x="tip.x" :y="tip.y">
        <template v-if="active > -1 && quads[active].data">
          <div class="dsh-tip__head">{{ quads[active].name }}</div>
          <div class="dsh-tip__value">{{ quads[active].pct === null ? "No aplica" : fmtPct(quads[active].pct) }}</div>
          <div class="dsh-tip__row">{{ fmtNum(quads[active].data.approvedScore) }} de {{ fmtNum(quads[active].data.requiredScore) }} puntos</div>
          <div class="dsh-tip__row">{{ quads[active].data.approvedItems }} de {{ quads[active].data.requiredItems }} ítems</div>
        </template>
      </DashTip>
    </div>

    <div class="dsh-wheel__side">
      <ul v-if="hasPhva" class="dsh-phva" aria-label="Cumplimiento por fase del ciclo PHVA">
        <li v-for="q in quads" :key="q.key">
          <router-link
            :to="to"
            class="dsh-phva__row"
            :class="{ 'is-active': active === q.i }"
            @mouseenter.native="focus(q.i)"
            @mouseleave.native="blur(q.i)"
            @focus.native="focus(q.i)"
            @blur.native="blur(q.i)"
          >
            <span class="dsh-phva__name">
              <strong>{{ q.name }}</strong>
              <small>{{ subtitle(q) }}</small>
            </span>
            <span class="dsh-phva__num">
              <template v-if="q.data && q.pct !== null">
                <strong>{{ fmtPct(q.pct, 1) }}</strong>
                <small>{{ fmtNum(q.data.approvedScore) }} de {{ fmtNum(q.data.requiredScore) }} pts · {{ q.data.approvedItems }}/{{ q.data.requiredItems }} ítems</small>
              </template>
              <template v-else>
                <strong class="is-na">—</strong>
                <small>No aplica a tu grupo</small>
              </template>
            </span>
          </router-link>
        </li>
      </ul>
      <DashEmpty
        v-else
        compact
        title="Aún no hay desglose por fase"
        text="Por ahora ves solo el cumplimiento total. El detalle de Planear, Hacer, Verificar y Actuar aparece cuando el servidor lo entregue."
      />
      <p class="dsh-note">
        Las marcas exteriores señalan 60&nbsp;% y 85&nbsp;%: los umbrales de la Res.&nbsp;0312/2019.
        <span v-if="scopeLabel">{{ scopeLabel }}</span>
      </p>
    </div>
  </div>
</template>
